import { Clear, DeleteSweep, Search as SearchIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { parsePaginatedResponse } from "../constants/pagination";
import { useRequirePermission } from "../hooks/useHasPermission";
import { activityLogsAPI, ticketsAPI } from "../services/api";
import { formatDate } from "../utils/dateFormatter";
import { PERMISSIONS } from "../utils/permissions";

const PAGE_SIZE = 50;

const emptyFilters = {
  users: [],
  events: [],
  dateFrom: "",
  dateTo: "",
  q: "",
};

const ActivityLogPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const hasAccess = useRequirePermission(
    PERMISSIONS.VIEW_ACTIVITY_LOG,
    onNavigate
  );
  const currentUser = useSelector((state) => state.auth.user);
  const isSuperuser = Boolean(currentUser?.is_superuser);

  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [eventTypes, setEventTypes] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeBefore, setPurgeBefore] = useState("");
  const [isPurging, setIsPurging] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const eventLabel = useCallback(
    (event) => (isRTL ? event.name_ar : event.name_en) || event.code,
    [isRTL]
  );

  const userLabel = useCallback(
    (user) => {
      const name = isRTL
        ? user.arabic_name || user.english_name
        : user.english_name || user.arabic_name;
      return name ? `${name} (${user.username})` : user.username;
    },
    [isRTL]
  );

  const loadLookups = useCallback(async () => {
    try {
      const [types, users] = await Promise.all([
        activityLogsAPI.eventTypes(),
        ticketsAPI.getUserSearchOptions(),
      ]);
      setEventTypes(Array.isArray(types) ? types : types?.results || []);
      setUserOptions(Array.isArray(users) ? users : []);
    } catch (err) {
      setError(err.message || t("activityLog.loadError"));
    }
  }, [t]);

  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        lang: i18n.language,
        user: appliedFilters.users,
        event: appliedFilters.events,
        date_from: appliedFilters.dateFrom,
        date_to: appliedFilters.dateTo,
        q: appliedFilters.q,
      };
      const response = await activityLogsAPI.list(params);
      const parsed = parsePaginatedResponse(response);
      setRows(parsed.items);
      setTotalCount(parsed.totalCount);
    } catch (err) {
      setError(err.message || t("activityLog.loadError"));
      setRows([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, i18n.language, page, t]);

  useEffect(() => {
    if (hasAccess) {
      loadLookups();
    }
  }, [hasAccess, loadLookups]);

  useEffect(() => {
    if (hasAccess) {
      loadLogs();
    }
  }, [hasAccess, loadLogs]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  const handlePurge = async () => {
    if (!purgeBefore) {
      return;
    }
    setIsPurging(true);
    try {
      const result = await activityLogsAPI.purge(purgeBefore);
      setPurgeOpen(false);
      setSnackbar({
        open: true,
        message: t("activityLog.purgeSuccess", { count: result.deleted ?? 0 }),
        severity: "success",
      });
      loadLogs();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || t("activityLog.purgeError"),
        severity: "error",
      });
    } finally {
      setIsPurging(false);
    }
  };

  const rowEventName = (row) =>
    isRTL ? row.event_name_ar : row.event_name_en;

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: "100%" }}
    >
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {t("activityLog.title")}
          </Typography>
          {isSuperuser && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={() => setPurgeOpen(true)}
            >
              {t("activityLog.purge")}
            </Button>
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1.4fr 1.2fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            <Autocomplete
              multiple
              options={userOptions}
              value={userOptions.filter((option) =>
                filters.users.includes(option.id)
              )}
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  users: value.map((item) => item.id),
                }))
              }
              getOptionLabel={userLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={userLabel(option)}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label={t("activityLog.users")} />
              )}
            />
            <Autocomplete
              multiple
              options={eventTypes}
              value={eventTypes.filter((option) =>
                filters.events.includes(option.event_id)
              )}
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  events: value.map((item) => item.event_id),
                }))
              }
              getOptionLabel={eventLabel}
              isOptionEqualToValue={(option, value) =>
                option.event_id === value.event_id
              }
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.event_id}
                    label={eventLabel(option)}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label={t("activityLog.events")} />
              )}
            />
            <DatePicker
              label={t("activityLog.dateFrom")}
              format="DD/MM/YYYY"
              value={
                filters.dateFrom && dayjs(filters.dateFrom).isValid()
                  ? dayjs(filters.dateFrom)
                  : null
              }
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  dateFrom: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                }))
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label={t("activityLog.dateTo")}
              format="DD/MM/YYYY"
              value={
                filters.dateTo && dayjs(filters.dateTo).isValid()
                  ? dayjs(filters.dateTo)
                  : null
              }
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  dateTo: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                }))
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 2,
              alignItems: "center",
            }}
          >
            <TextField
              label={t("activityLog.reference")}
              value={filters.q}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, q: event.target.value }))
              }
              onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              sx={{ minWidth: { xs: "100%", sm: 240 } }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              {t("activityLog.search")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClear}
            >
              {t("activityLog.clear")}
            </Button>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("activityLog.dateTime")}</TableCell>
                  <TableCell>{t("activityLog.user")}</TableCell>
                  <TableCell>{t("activityLog.event")}</TableCell>
                  <TableCell>{t("activityLog.details")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary">
                        {t("activityLog.noRecords")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.log_id}
                      sx={
                        row.event_code === "error"
                          ? {
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(255, 167, 38, 0.16)"
                                  : "rgba(255, 167, 38, 0.12)",
                            }
                          : undefined
                      }
                    >
                      <TableCell>{formatDate(row.created_at)}</TableCell>
                      <TableCell>{row.user_name || "—"}</TableCell>
                      <TableCell>{rowEventName(row)}</TableCell>
                      <TableCell>
                        {[row.reference, row.detail]
                          .filter(Boolean)
                          .join(" — ") || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && totalCount > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t("referencePages.paginationShowing", {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, totalCount),
                total: totalCount,
              })}
            </Typography>
            <Pagination
              count={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
              showFirstButton
              showLastButton
              dir={isRTL ? "rtl" : "ltr"}
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={purgeOpen}
        onClose={() => !isPurging && setPurgeOpen(false)}
        fullWidth
        maxWidth="sm"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("activityLog.purgeTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("activityLog.purgeConfirm")}
          </DialogContentText>
          <DatePicker
            label={t("activityLog.purgeBefore")}
            format="DD/MM/YYYY"
            value={
              purgeBefore && dayjs(purgeBefore).isValid()
                ? dayjs(purgeBefore)
                : null
            }
            onChange={(value) =>
              setPurgeBefore(
                value && value.isValid() ? value.format("YYYY-MM-DD") : ""
              )
            }
            slotProps={{ textField: { fullWidth: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)} disabled={isPurging}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handlePurge}
            disabled={isPurging || !purgeBefore}
          >
            {t("activityLog.purge")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActivityLogPage;
